import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCatalogStructure, getProgressSummary } from '../api';
import { findGradeLevel, getMajorTopics, getSkills } from '../utils/catalog';
import { buildStandardStats, summariseSkills } from '../utils/progress';
import { saveRecentActivity } from '../utils/recentActivity';

function GradeTopicsPage({ authEmail, authPassword }) {
  const { stateName, gradeName } = useParams();
  const navigate = useNavigate();
  const decodedState = decodeURIComponent(stateName || '');
  const decodedGrade = decodeURIComponent(gradeName || '');

  const [catalog, setCatalog] = useState(null);
  const [standardStats, setStandardStats] = useState(() => new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const summaryPromise = authEmail && authPassword
      ? getProgressSummary(authEmail, authPassword, {
          state: decodedState,
          gradeLevel: decodedGrade,
        }).catch(() => ({ summary: [] }))
      : Promise.resolve({ summary: [] });

    Promise.all([getCatalogStructure(), summaryPromise])
      .then(([data, summaryData]) => {
        if (cancelled) return;
        setCatalog(data);
        setStandardStats(buildStandardStats(summaryData.summary, {
          state: decodedState,
          grade: decodedGrade,
        }));
        setLoading(false);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load topics');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [authEmail, authPassword, decodedState, decodedGrade]);

  useEffect(() => {
    if (!decodedGrade) return;
    saveRecentActivity({
      state: decodedState,
      grade: decodedGrade,
      path: `/state/${encodeURIComponent(decodedState)}/grade/${encodeURIComponent(decodedGrade)}`,
    });
  }, [decodedState, decodedGrade]);

  const gradeLevel = useMemo(
    () => findGradeLevel(catalog, decodedState, decodedGrade),
    [catalog, decodedState, decodedGrade]
  );

  const paths = useMemo(() => (
    getMajorTopics(gradeLevel).map((majorTopic) => ({
      majorTopic,
      summary: summariseSkills(getSkills(majorTopic), standardStats),
    }))
  ), [gradeLevel, standardStats]);

  const overall = useMemo(() => {
    const total = paths.reduce((sum, item) => sum + item.summary.total, 0);
    const completed = paths.reduce((sum, item) => sum + item.summary.completed, 0);
    return {
      total,
      completed,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [paths]);

  const openMajorTopic = (majorTopicName) => {
    navigate(
      `/state/${encodeURIComponent(decodedState)}/grade/${encodeURIComponent(decodedGrade)}` +
      `/topic/${encodeURIComponent(majorTopicName)}`
    );
  };

  return (
    <div className="browse-page-container">
      <button type="button" className="back-button" onClick={() => navigate('/')}>
        ← Back to Home
      </button>

      <div className="browse-page-header">
        <h1 className="browse-page-title">{decodedGrade} learning path</h1>
        <p className="browse-page-subtitle">
          {decodedState ? `${decodedState} · ` : ''}
          Work through one topic at a time. Pick up wherever you left off.
        </p>
      </div>

      

      {!loading && !error && overall.total > 0 && (
        <div className="path-overview">
          <div className="path-overview-text">
            <span className="path-overview-count">
              {overall.completed} of {overall.total} skills completed
            </span>
            <span className="path-overview-hint">
              {authEmail
                ? 'A skill is complete once you get all questions right.'
                : 'Log in to save your progress and track completed skills.'}
            </span>
          </div>
          <div className="progress-track path-overview-track">
            <span className="progress-fill" style={{ width: `${overall.percentage}%` }} />
          </div>
        </div>
      )}

      {loading && (
        <div className="browse-loading">
          <div className="browse-loading-spinner" />
          <span>Loading your learning path…</span>
        </div>
      )}

      {error && <div className="error">{error}</div>}

      {!loading && !error && paths.length === 0 && (
        <div className="browse-empty">No topics found for this grade level.</div>
      )}

      {!loading && !error && paths.length > 0 && (
        <div className="learning-path">
          {paths.map(({ majorTopic, summary }, index) => {
            const done = summary.total > 0 && summary.completed === summary.total;
            const cta = done ? 'Review' : summary.started ? 'Continue' : 'Start';

            return (
              <button
                key={majorTopic.name}
                type="button"
                className={`path-card${done ? ' path-card-done' : ''}`}
                onClick={() => openMajorTopic(majorTopic.name)}
              >
                <span className="path-card-step">{done ? '✓' : index + 1}</span>
                <span className="path-card-main">
                  <span className="path-card-title">{majorTopic.name}</span>
                  <span className="progress-track">
                    <span
                      className="progress-fill"
                      style={{ width: `${summary.percentage}%` }}
                    />
                  </span>
                  <span className="path-card-meta">
                    {summary.completed}/{summary.total} skill
                    {summary.total !== 1 ? 's' : ''} completed
                  </span>
                </span>
                <span className="path-card-cta">
                  {cta}
                  <span className="path-card-arrow">→</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      {decodedState.toLowerCase() === 'massachusetts' && (
        <div className="test-invitation">
          <div>
            <h2>Try a {decodedGrade} sample test</h2>
            <p>Put your skills together in an MCAS-style exam, then review your answers.</p>
          </div>
          <button type="button" className="test-primary" onClick={() => navigate(
            `/state/${encodeURIComponent(decodedState)}/grade/${encodeURIComponent(decodedGrade)}/practice-test`
          )}>Open sample test →</button>
        </div>
      )}
    </div>
  );
}

export default GradeTopicsPage;
